import React, { useState } from 'react';
import { PlusIcon, CheckIcon, XIcon, EditIcon, RefreshCwIcon, BellIcon, MessageSquareIcon, MailIcon, GlobeIcon, ToggleLeftIcon, ToggleRightIcon } from 'lucide-react';
import { NotificationChannel, NotificationScope, TeamsConfig, SlackConfig, EmailConfig, WebhookConfig } from './NotificationsManagement';
import { StatusBadge } from '../../StatusBadge';
import { OrgUnitSelector } from '../orgunit/OrgUnitSelector';
import type { OrgUnit } from '../../financial/sharedFinancialData';
interface NotificationChannelsProps {
  globalChannels: NotificationChannel[];
  orgUnitChannels: NotificationChannel[];
  setChannels: React.Dispatch<React.SetStateAction<NotificationChannel[]>>;
  locale: string;
}
export function NotificationChannels({
  globalChannels,
  orgUnitChannels,
  setChannels,
  locale
}: NotificationChannelsProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<NotificationChannel | null>(null);
  const [testResult, setTestResult] = useState<{
    channelId: string;
    success: boolean;
    message: string;
  } | null>(null);
  const [newChannelScope, setNewChannelScope] = useState<NotificationScope>('Global');
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnit | null>(null);
  const allChannels = [...globalChannels, ...orgUnitChannels];
  const getChannelIcon = (type: string) => {
    switch (type) {
      case 'teams':
        return <MessageSquareIcon className="h-5 w-5 text-blue-500" data-id="element-19579" />;
      case 'slack':
        return <MessageSquareIcon className="h-5 w-5 text-purple-500" data-id="element-19580" />;
      case 'email':
        return <MailIcon className="h-5 w-5 text-gray-500" data-id="element-19581" />;
      case 'webhook':
        return <GlobeIcon className="h-5 w-5 text-green-500" data-id="element-19582" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-500" data-id="element-19583" />;
    }
  };
  const handleToggleChannel = (channelId: string) => {
    setChannels(channels => channels.map(channel => channel.id === channelId ? {
      ...channel,
      isEnabled: !channel.isEnabled
    } : channel));
  };
  const handleEditChannel = (channel: NotificationChannel) => {
    setEditingChannel(channel);
    setNewChannelScope(channel.scope);
    setIsDrawerOpen(true);
  };
  const handleTestConnection = (channelId: string) => {
    const channel = allChannels.find(c => c.id === channelId);
    if (!channel || !channel.isConfigured) {
      setTestResult({
        channelId,
        success: false,
        message: 'Channel is not properly configured.'
      });
      return;
    }
    setTimeout(() => {
      const success = Math.random() > 0.3;
      setTestResult({
        channelId,
        success,
        message: success ? 'Test message sent successfully!' : 'Failed to send test message. Please check your configuration.'
      });
    }, 1000);
  };
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingChannel(null);
    setNewChannelScope('Global');
    setSelectedOrgUnit(null);
  };
  const handleSaveChannel = (updatedChannel: NotificationChannel) => {
    setChannels(channels => channels.map(channel => channel.id === updatedChannel.id ? updatedChannel : channel));
    setIsDrawerOpen(false);
    setEditingChannel(null);
  };
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    setSelectedOrgUnit(orgUnit);
  };
  const renderChannelCard = (channel: NotificationChannel, showOrgUnit: boolean = false) => {
    return <div key={channel.id} className="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200" data-id="element-19584">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-start" data-id="element-19585">
          <div className="flex-1" data-id="element-19586">
            {showOrgUnit && channel.orgUnitName && <div className="mb-2" data-id="element-19587">
                <p className="text-xs font-medium text-gray-500" data-id="element-19588">
                  {channel.orgUnitName}
                </p>
                <p className="text-xs text-gray-400" data-id="element-19589">{channel.orgUnitPath}</p>
              </div>}
            <div className="flex items-center" data-id="element-19590">
              {getChannelIcon(channel.type)}
              <h3 className="ml-2 text-lg leading-6 font-medium text-gray-900" data-id="element-19591">
                {channel.name}
              </h3>
            </div>
          </div>
          <div data-id="element-19592">
            <button type="button" onClick={() => handleToggleChannel(channel.id)} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19593">
              {channel.isEnabled ? <ToggleRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-19594" /> : <ToggleLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-19595" />}
            </button>
          </div>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-19596">
          <div className="flex justify-between items-center mb-4" data-id="element-19597">
            <div className="flex space-x-2" data-id="element-19598">
              <StatusBadge status={channel.isEnabled ? 'success' : 'error'} text={channel.isEnabled ? 'Enabled' : 'Disabled'} data-id="element-19599" />
              <StatusBadge status={channel.isConfigured ? 'success' : 'warning'} text={channel.isConfigured ? 'Configured' : 'Not Configured'} data-id="element-19600" />
            </div>
          </div>
          {channel.type === 'teams' && <div className="mt-2 text-sm text-gray-500" data-id="element-19601">
              <p data-id="element-19602">
                Microsoft Teams integration for sending notifications directly
                to users or channels.
              </p>
              {channel.isConfigured && <div className="mt-2" data-id="element-19603">
                  <p data-id="element-19604">
                    Delivery Method:{' '}
                    {(channel.configuration as TeamsConfig).deliveryMethod === 'direct' ? 'Direct Message' : 'Channel Post'}
                  </p>
                  {(channel.configuration as TeamsConfig).defaultChannel && <p data-id="element-19605">
                      Default Channel:{' '}
                      {(channel.configuration as TeamsConfig).defaultChannel}
                    </p>}
                </div>}
            </div>}
          {channel.type === 'slack' && <div className="mt-2 text-sm text-gray-500" data-id="element-19606">
              <p data-id="element-19607">
                Slack integration for sending notifications to workspace
                channels.
              </p>
              {channel.isConfigured && <div className="mt-2" data-id="element-19608">
                  <p data-id="element-19609">
                    Default Channel:{' '}
                    {(channel.configuration as SlackConfig).defaultChannel}
                  </p>
                </div>}
            </div>}
          {channel.type === 'email' && <div className="mt-2 text-sm text-gray-500" data-id="element-19610">
              <p data-id="element-19611">Email notifications sent to user email addresses.</p>
              {channel.isConfigured && <div className="mt-2" data-id="element-19612">
                  <p data-id="element-19613">
                    From: {(channel.configuration as EmailConfig).fromAddress}
                  </p>
                  <p data-id="element-19614">
                    Connection:{' '}
                    {(channel.configuration as EmailConfig).connectionType}
                  </p>
                </div>}
            </div>}
          {channel.type === 'webhook' && <div className="mt-2 text-sm text-gray-500" data-id="element-19615">
              <p data-id="element-19616">
                Custom webhook integration for sending notifications to external
                systems.
              </p>
              {channel.isConfigured && <div className="mt-2" data-id="element-19617">
                  <p data-id="element-19618">
                    URL:{' '}
                    {(channel.configuration as WebhookConfig).url.substring(0, 30)}
                    ...
                  </p>
                  <p data-id="element-19619">
                    Auth Type:{' '}
                    {(channel.configuration as WebhookConfig).authType}
                  </p>
                </div>}
            </div>}
          {testResult && testResult.channelId === channel.id && <div className={`mt-3 p-2 text-sm rounded-md ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-19620">
              {testResult.message}
            </div>}
          <div className="mt-4 flex space-x-3" data-id="element-19621">
            <button type="button" onClick={() => handleEditChannel(channel)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19622">
              <EditIcon className="h-4 w-4 mr-2" data-id="element-19623" />
              Configure
            </button>
            <button type="button" onClick={() => handleTestConnection(channel.id)} disabled={!channel.isConfigured} className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${channel.isConfigured ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-indigo-500' : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'}`} data-id="element-19624">
              <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-19625" />
              Test Connection
            </button>
          </div>
        </div>
      </div>;
  };
  return <div className="space-y-8" data-id="element-19626">
      {/* Global Channels */}
      <div data-id="element-19627">
        <div className="mb-6 flex justify-between items-center" data-id="element-19628">
          <div data-id="element-19629">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19630">
              Global Notification Channels
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-19631">
              Global channels available to all organisation units
            </p>
          </div>
          <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19632">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-19633" />
            Add Custom Channel
          </button>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2" data-id="element-19634">
          {globalChannels.map(channel => renderChannelCard(channel, false))}
        </div>
      </div>

      {/* OrgUnit Channels */}
      <div data-id="element-19635">
        <div className="mb-6" data-id="element-19636">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19637">
            Organisation Unit Channels
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19638">
            Channels specific to individual organisation units
          </p>
        </div>
        {orgUnitChannels.length === 0 ? <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-19639">
            <div className="px-4 py-12 text-center" data-id="element-19640">
              <p className="text-sm text-gray-500" data-id="element-19641">
                No organisation unit channels configured.
              </p>
            </div>
          </div> : <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2" data-id="element-19642">
            {orgUnitChannels.map(channel => renderChannelCard(channel, true))}
          </div>}
      </div>

      {/* Configuration Drawer */}
      {isDrawerOpen && editingChannel && <div className="fixed inset-0 overflow-hidden z-50" data-id="element-19643">
          <div className="absolute inset-0 overflow-hidden" data-id="element-19644">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDrawer} data-id="element-19645"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-19646">
              <div className="relative w-screen max-w-md" data-id="element-19647">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll" data-id="element-19648">
                  <div className="px-4 py-6 sm:px-6" data-id="element-19649">
                    <div className="flex items-start justify-between" data-id="element-19650">
                      <h2 className="text-lg font-medium text-gray-900" data-id="element-19651">
                        Configure {editingChannel.name}
                      </h2>
                      <div className="ml-3 h-7 flex items-center" data-id="element-19652">
                        <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-19653">
                          <span className="sr-only" data-id="element-19654">Close panel</span>
                          <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-19655" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 py-6 overflow-y-auto px-4 sm:px-6" data-id="element-19656">
                    <div className="space-y-6" data-id="element-19657">
                      {/* Scope Selector */}
                      <div data-id="element-19658">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19659">
                          Scope <span className="text-red-500" data-id="element-19660">*</span>
                        </label>
                        <select value={newChannelScope} onChange={e => setNewChannelScope(e.target.value as NotificationScope)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19661">
                          <option value="Global" data-id="element-19662">Global</option>
                          <option value="OrgUnit" data-id="element-19663">Organisation Unit</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500" data-id="element-19664">
                          {newChannelScope === 'Global' ? 'Available to all organisation units' : 'Specific to selected organisation unit'}
                        </p>
                      </div>

                      {/* OrgUnit Selector */}
                      {newChannelScope === 'OrgUnit' && <div data-id="element-19665">
                          <OrgUnitSelector value={selectedOrgUnit?.id || ''} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} data-id="element-19666" />
                        </div>}

                      <p className="text-sm text-gray-500" data-id="element-19667">
                        Configure your {editingChannel.name} integration
                        settings.
                      </p>
                      <div className="space-y-6" data-id="element-19668">
                        <p className="text-sm text-gray-500" data-id="element-19669">
                          In a real implementation, this would contain the
                          appropriate form fields for configuring{' '}
                          {editingChannel.type} integration.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 px-4 py-4 flex justify-end border-t border-gray-200" data-id="element-19670">
                    <button type="button" className="mr-3 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-19671">
                      Cancel
                    </button>
                    <button type="button" className="bg-indigo-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={() => {
                  const updatedChannel = {
                    ...editingChannel,
                    isConfigured: true,
                    scope: newChannelScope,
                    ...(newChannelScope === 'OrgUnit' && selectedOrgUnit ? {
                      orgUnitId: selectedOrgUnit.id,
                      orgUnitName: selectedOrgUnit.name,
                      orgUnitPath: selectedOrgUnit.path
                    } : {
                      orgUnitId: undefined,
                      orgUnitName: undefined,
                      orgUnitPath: undefined
                    })
                  };
                  handleSaveChannel(updatedChannel);
                }} data-id="element-19672">
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}